// src/components/pages/.../InquiryFormPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Chip,
  TableContainer,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Link as MLink,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { InquiryAPI } from "../../../../../api/Inquiry";
import { useAuth } from "../../../Auth/AuthContext";
import { useGlobalSnackbar } from "../../../../GlobalFunction/GlobalSnackbarProvider";
import {
  getLangFromCookie,
  LANG_COOKIE,
} from "../../../../../Utils/LangCookie";
import { resolveAssetUrl } from "../../../../../lib/http";
import { resolveCanonicalUserID } from "../../../../../Utils/UserIdentity";
import SuccessInquiry from "./SuccessInquiry";

const font = {
  ZH: "Line Seed TW",
  EN: "Line Seed Sans",
  JP: "Line Seed JP",
};

const PLACEHOLDER_IMG = "/img/placeholder.png";

/* ------------------------------ helpers ------------------------------ */

function normalizeLang(l) {
  const s = String(l || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function resolveUserID(user) {
  return resolveCanonicalUserID(user);
}

function toUrl(v) {
  if (!v) return null;
  if (typeof v === "string") return v;

  if (typeof v === "object") {
    if (v.url) return v.url;
    if (v.image) return v.image;
    if (v.path) return v.path;
    if (v.src) return v.src;

    if (v.sourceUrl) return v.sourceUrl;
    if (v.mediaItemUrl) return v.mediaItemUrl;

    if (v.node && (v.node.sourceUrl || v.node.mediaItemUrl))
      return v.node.sourceUrl || v.node.mediaItemUrl;
  }

  return null;
}

function extractImages(row) {
  const raw =
    row?.images || row?.product?.images || row?.product?.main?.images || [];
  const list = Array.isArray(raw) ? raw : [];
  const urls = list
    .map(toUrl)
    .filter(Boolean)
    .map((u) => resolveAssetUrl(String(u)))
    .filter(Boolean);

  if (!urls.length) {
    const one =
      toUrl(row?.image) ||
      toUrl(row?.imageUrl) ||
      toUrl(row?.product?.image) ||
      toUrl(row?.product?.imageUrl) ||
      null;

    if (one) return [resolveAssetUrl(String(one))];
  }

  return urls.length ? urls : [PLACEHOLDER_IMG];
}

const isBlank = (v) => !String(v ?? "").trim();

const normalizeEmail = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

const isValidEmail = (v) => {
  const email = normalizeEmail(v);
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
};

const phoneDigits = (v) => String(v ?? "").replace(/[^\d]/g, "");
const isValidPhone = (v) => {
  const digits = phoneDigits(v);
  return digits.length >= 6 && digits.length <= 20;
};

function uniqNonBlank(list) {
  const out = [];
  const seen = new Set();
  for (const v of list || []) {
    const s = String(v ?? "").trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/* ------------------------------ Industry options ------------------------------ */
/**
 * ✅ Requirement:
 * - UI shows localized labels (ZH/EN/JP)
 * - value stored/sent to backend ALWAYS uses Mandarin (ZH) value
 */
const INDUSTRY_OPTIONS = [
  { ZH: "食品工廠", EN: "Food Manufacturing", JP: "食品製造工場" },
  { ZH: "連鎖餐廳", EN: "Restaurant Chains", JP: "外食チェーン" },
  { ZH: "量販店", EN: "Hypermarkets", JP: "量販店" },
  {
    ZH: "超市/便利超商",
    EN: "Supermarkets / Convenience Stores",
    JP: "スーパーマーケット／コンビニエンスストア",
  },
  { ZH: "飯店/民宿", EN: "Hotels", JP: "ホテル・旅館/民泊・簡易宿所" },
];

function industryLabelByLang(opt, lang) {
  if (!opt) return "";
  if (lang === "EN") return opt.EN || opt.ZH;
  if (lang === "JP") return opt.JP || opt.ZH;
  return opt.ZH;
}

/* ------------------------------ Gallery Dialog ------------------------------ */

function ImageGalleryDialog({ open, onClose, title, images = [], lang }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const safeImages = useMemo(() => {
    const uniq = [];
    const seen = new Set();
    for (const u of images || []) {
      const s = String(u || "");
      if (!s) continue;
      if (seen.has(s)) continue;
      seen.add(s);
      uniq.push(s);
    }
    return uniq.length ? uniq : [PLACEHOLDER_IMG];
  }, [images]);

  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
  }, [open, safeImages.length]);

  const mainSrc =
    safeImages[Math.min(activeIdx, safeImages.length - 1)] || PLACEHOLDER_IMG;

  const scrollerRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const dragRef = useRef({
    active: false,
    pointerId: null,
    captured: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });

  const DRAG_THRESHOLD = 6;

  const checkScrollable = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setIsScrollable(el.scrollWidth > el.clientWidth + 2);
  };

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(checkScrollable);
  }, [open, safeImages.length]);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const el = scrollerRef.current;
    if (!el) return;

    dragRef.current.active = true;
    dragRef.current.pointerId = e.pointerId;
    dragRef.current.captured = false;
    dragRef.current.startX = e.clientX;
    dragRef.current.startScrollLeft = el.scrollLeft;
    dragRef.current.moved = false;
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const el = scrollerRef.current;
    if (!el) return;

    const dx = e.clientX - dragRef.current.startX;

    if (!dragRef.current.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      dragRef.current.moved = true;

      if (!dragRef.current.captured) {
        try {
          e.currentTarget.setPointerCapture(dragRef.current.pointerId);
          dragRef.current.captured = true;
        } catch {}
      }
    }

    el.scrollLeft = dragRef.current.startScrollLeft - dx;

    try {
      e.preventDefault();
    } catch {}
  };

  const onPointerUp = () => {
    if (!dragRef.current.active) return;

    dragRef.current.active = false;

    if (dragRef.current.captured) {
      try {
        scrollerRef.current?.releasePointerCapture(dragRef.current.pointerId);
      } catch {}
    }

    dragRef.current.pointerId = null;
    dragRef.current.captured = false;

    requestAnimationFrame(checkScrollable);
  };

  const safeThumbClick = (fn) => {
    if (dragRef.current.moved) return;
    fn?.();
  };

  const THUMB = isMdUp ? 86 : 76;
  const GAP = isMdUp ? 12 : 10;

  useEffect(() => {
    if (!open) return;
    const el = scrollerRef.current;
    if (!el) return;

    const node = el.querySelector(`[data-thumb-idx="${activeIdx}"]`);
    if (!node) return;

    const left = node.offsetLeft;
    const w = node.offsetWidth;
    const target = left - (el.clientWidth / 2 - w / 2);
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeIdx, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          position: "relative",
          overflow: "visible",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: { xs: -40, sm: -50 },
          right: { xs: -10, sm: 0 },
          zIndex: 10,
          color: "primary",
          width: { xs: 42, sm: 46 },
          height: { xs: 42, sm: 46 },
          "&:hover": { bgcolor: "secondary" },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 26 }} />
      </IconButton>

      <DialogTitle sx={{ pr: 2, fontWeight: 700, fontFamily: font[lang] }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontFamily: font[lang],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "wrap",
          }}
        >
          {title || "Images"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            height: { xs: 260, sm: 360, md: 440 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={mainSrc}
            alt="product"
            onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />

          {safeImages.length > 1 && (
            <>
              <IconButton
                onClick={() =>
                  setActiveIdx(
                    (p) => (p - 1 + safeImages.length) % safeImages.length,
                  )
                }
                sx={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(255,255,255,0.85)",
                  boxShadow: 1,
                }}
              >
                <ChevronLeftRounded />
              </IconButton>

              <IconButton
                onClick={() => setActiveIdx((p) => (p + 1) % safeImages.length)}
                sx={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(255,255,255,0.85)",
                  boxShadow: 1,
                }}
              >
                <ChevronRightRounded />
              </IconButton>
            </>
          )}

          {safeImages.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                bottom: 10,
                transform: "translateX(-50%)",
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {activeIdx + 1}/{safeImages.length}
            </Box>
          )}
        </Box>

        {safeImages.length > 1 && (
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: THUMB + 18,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                ref={scrollerRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                sx={{
                  flex: 1,
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                  touchAction: "pan-x",
                  userSelect: "none",
                  cursor: isScrollable ? "grab" : "default",
                  "&:active": { cursor: isScrollable ? "grabbing" : "default" },
                  px: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: `${GAP}px`,
                    py: 1,
                    minWidth: "100%",
                  }}
                >
                  {safeImages.map((src, i) => {
                    const selected = i === activeIdx;
                    return (
                      <Box
                        key={`${src}-${i}`}
                        data-thumb-idx={i}
                        onClick={() => safeThumbClick(() => setActiveIdx(i))}
                        sx={{
                          flex: "0 0 auto",
                          width: THUMB,
                          height: THUMB,
                          borderRadius: 1.5,
                          border: "2px solid",
                          borderColor: selected ? "primary.main" : "divider",
                          bgcolor: "#fff",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt={`thumb-${i}`}
                          onError={(e) =>
                            (e.currentTarget.src = PLACEHOLDER_IMG)
                          }
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- i18n ---------------- */

const I18N = {
  title: {
    ZH: "填寫詢價資料",
    EN: "Inquiry Information",
    JP: "ご連絡先情報入力",
  },
  step1Title: {
    ZH: "確認您的詢價產品",
    EN: "Confirm your inquiry products",
    JP: "見積り製品の確認",
  },
  step2Title: {
    ZH: "填寫詢價資料 & 送出詢價單",
    EN: "Fill in details & submit inquiry",
    JP: "必要な情報の入力&依頼書の送信",
  },
  table: {
    no: { ZH: "項次", EN: "No.", JP: "No." },
    image: { ZH: "圖片", EN: "Image", JP: "画像" },
    series: { ZH: "產品型號", EN: "Series Number", JP: "型番" },
    name: { ZH: "產品名稱", EN: "Product", JP: "品名" },
    qty: { ZH: "數量", EN: "Qty", JP: "数量" },
  },
  fields: {
    name: { ZH: "*姓名", EN: "*Name", JP: "*氏名" },
    phone: { ZH: "*聯絡電話", EN: "*Phone", JP: "*電話番号" },
    email: { ZH: "*電子郵件", EN: "*Email", JP: "*電子メール" },
    county: { ZH: "*縣市", EN: "*County/City", JP: "*県・市" },
    address: { ZH: "*地址", EN: "*Address", JP: "*住所" },
    company: { ZH: "*公司名稱", EN: "*Company name", JP: "*会社名" },
    industry: { ZH: "*產業別", EN: "*Industry", JP: "*業種" },
    message: { ZH: "訊息", EN: "Message", JP: "メッセージ" },
  },
  actions: {
    back: { ZH: "返回詢價車", EN: "Back To Inquiry", JP: "見積一覧へ" },
    submit: { ZH: "送出", EN: "Submit", JP: "送信する" },
  },
  confirm: {
    title: { ZH: "確認送出", EN: "Confirm submission", JP: "送信確認" },
    desc: {
      ZH: "確定要送出詢價單嗎？送出後將進入處理流程。",
      EN: "Are you sure you want to submit this inquiry? It will be processed after submission.",
      JP: "この内容で送信します。よろしいですか？",
    },
    cancel: { ZH: "取消", EN: "Cancel", JP: "キャンセル" },
    confirm: { ZH: "確定送出", EN: "Submit", JP: "送信する" },
  },
  required: {
    ZH: "此欄位為必填",
    EN: "This field is required",
    JP: "必須項目です",
  },
  invalidEmail: {
    ZH: "請輸入有效的電子郵件格式",
    EN: "Please enter a valid email address",
    JP: "有効なメールアドレスを入力してください",
  },
  invalidPhone: {
    ZH: "請輸入有效的電話號碼（至少 6 位數字）",
    EN: "Please enter a valid phone number (at least 6 digits)",
    JP: "有効な電話番号を入力してください（6桁以上）",
  },
  snackbar: {
    success: {
      ZH: "詢價送出成功",
      EN: "Inquiry submitted successfully.",
      JP: "見積り依頼を送信しました。",
    },
    fail: {
      ZH: "送出失敗，請稍後再試",
      EN: "Submit failed. Please try again later.",
      JP: "送信に失敗しました。しばらくしてから再度お試しください。",
    },
  },
  viewImg: { ZH: "看圖片", EN: "View images", JP: "画像を見る" },
};

const COUNTIES_ZH = [
  "臺北市",
  "新北市",
  "桃園市",
  "臺中市",
  "臺南市",
  "高雄市",
  "基隆市",
  "新竹市",
  "新竹縣",
  "苗栗縣",
  "彰化縣",
  "南投縣",
  "雲林縣",
  "嘉義市",
  "嘉義縣",
  "屏東縣",
  "宜蘭縣",
  "花蓮縣",
  "臺東縣",
];

const COUNTY_MAP = [
  { ZH: "臺北市", EN: "Taipei City", JP: "台北市" },
  { ZH: "新北市", EN: "New Taipei City", JP: "新北市" },
  { ZH: "桃園市", EN: "Taoyuan City", JP: "桃園市" },
  { ZH: "臺中市", EN: "Taichung City", JP: "台中市" },
  { ZH: "臺南市", EN: "Tainan City", JP: "台南市" },
  { ZH: "高雄市", EN: "Kaohsiung City", JP: "高雄市" },
  { ZH: "基隆市", EN: "Keelung City", JP: "基隆市" },
  { ZH: "新竹市", EN: "Hsinchu City", JP: "新竹市" },
  { ZH: "新竹縣", EN: "Hsinchu County", JP: "新竹県" },
  { ZH: "苗栗縣", EN: "Miaoli County", JP: "苗栗県" },
  { ZH: "彰化縣", EN: "Changhua County", JP: "彰化県" },
  { ZH: "南投縣", EN: "Nantou County", JP: "南投県" },
  { ZH: "雲林縣", EN: "Yunlin County", JP: "雲林県" },
  { ZH: "嘉義市", EN: "Chiayi City", JP: "嘉義市" },
  { ZH: "嘉義縣", EN: "Chiayi County", JP: "嘉義県" },
  { ZH: "屏東縣", EN: "Pingtung County", JP: "屏東県" },
  { ZH: "宜蘭縣", EN: "Yilan County", JP: "宜蘭県" },
  { ZH: "花蓮縣", EN: "Hualien County", JP: "花蓮県" },
  { ZH: "臺東縣", EN: "Taitung County", JP: "台東県" },
];

const byLangCounty = (zh, lang) => {
  const hit = COUNTY_MAP.find((x) => x.ZH === zh);
  if (!hit) return zh;
  return lang === "EN" ? hit.EN : lang === "JP" ? hit.JP : hit.ZH;
};

function MobileInquiryItemCard({ row, lang, t, onOpenImages, onGoProduct }) {
  const imgs = extractImages(row);
  const thumb = imgs[0] || PLACEHOLDER_IMG;

  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Tooltip title={t.viewImg[lang]} arrow>
            <Box
              component="button"
              onClick={onOpenImages}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <Box
                component="img"
                src={thumb}
                alt={String(row?.productName || row?.productID || "")}
                onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                sx={{
                  width: 72,
                  height: 54,
                  objectFit: "contain",
                  p: 0.5,
                  flexShrink: 0,
                  display: "block",
                }}
              />
            </Box>
          </Tooltip>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: font[lang],
                fontSize: 12,
                fontWeight: 900,
                color: "text.secondary",
                mb: 0.25,
              }}
            >
              {t.table.series[lang]}
            </Typography>
            <Typography
              sx={{
                fontFamily: font[lang],
                fontSize: 13,
                fontWeight: 900,
                overflowWrap: "anywhere",
              }}
            >
              {String(row?.productID || "-")}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontFamily: font[lang],
                fontSize: 12,
                fontWeight: 900,
                color: "text.secondary",
                mb: 0.25,
              }}
            >
              {t.table.qty[lang]}
            </Typography>
            <Typography
              sx={{ fontFamily: font[lang], fontSize: 13, fontWeight: 900 }}
            >
              {String(row?.qty ?? "-")}
            </Typography>
          </Box>
        </Stack>

        <Box>
          <Typography
            sx={{
              fontFamily: font[lang],
              fontSize: 12,
              fontWeight: 900,
              color: "text.secondary",
              mb: 0.25,
            }}
          >
            {t.table.name[lang]}
          </Typography>

          {typeof onGoProduct === "function" && row?.parentID ? (
            <MLink
              component="button"
              underline="hover"
              onClick={onGoProduct}
              sx={{
                fontFamily: font[lang],
                fontSize: 13,
                fontWeight: 800,
                textAlign: "left",
                cursor: "pointer",
                color: "primary.main",
                overflowWrap: "anywhere",
              }}
            >
              {String(row?.productName || "-")}
            </MLink>
          ) : (
            <Typography
              sx={{
                fontFamily: font[lang],
                fontSize: 13,
                fontWeight: 800,
                overflowWrap: "anywhere",
              }}
            >
              {String(row?.productName || "-")}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

/* ---------------- component ---------------- */

export default function InquiryFormPage() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const nav = useNavigate();
  const loc = useLocation();
  const { showSnackbar } = useGlobalSnackbar();
  const { user, loading: authLoading } = useAuth();

  const userID = useMemo(() => {
    const uid = resolveUserID(user);
    return uid ? String(uid) : null;
  }, [user]);

  const [lang, setLang] = useState(() => normalizeLang(getLangFromCookie()));
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const syncLang = () => {
      const c = normalizeLang(
        getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH",
      );
      setLang(c);
    };
    syncLang();
    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  const t = I18N;

  const [inq, setInq] = useState(null);
  const [form, setForm] = useState({
    姓名: "",
    聯絡電話: "",
    電子郵件: "",
    縣市: "",
    地址: "",
    公司名稱: "",
    產業別: "",
    訊息: "",
  });

  const [suggest, setSuggest] = useState({
    姓名: [],
    聯絡電話: [],
    電子郵件: [],
  });

  const [attempted, setAttempted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [imgDlgOpen, setImgDlgOpen] = useState(false);
  const [imgDlgTitle, setImgDlgTitle] = useState("");
  const [imgDlgImages, setImgDlgImages] = useState([]);

  const openImageDialog = (row) => {
    const imgs = extractImages(row);
    setImgDlgImages(imgs);
    setImgDlgTitle(row?.productName || row?.productID || "Images");
    setImgDlgOpen(true);
  };

  const goToProduct = (row) => {
    const parentID = row?.parentID || null;
    const variantID = row?.productID || null;
    if (!parentID || !variantID) return;

    nav(
      `/商品資訊/${encodeURIComponent(parentID)}?variant=${encodeURIComponent(variantID)}`,
      {
        state: {
          fromBreadcrumbs: [{ label: "商品資訊", href: "b2b/商品資訊" }],
        },
      },
    );
  };

  useEffect(() => {
    if (!authLoading && !user) {
      nav("/auth", { replace: false, state: { redirectTo: loc.pathname } });
    }
  }, [authLoading, user, nav, loc.pathname]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!userID) return;

      try {
        const { inquiry } = await InquiryAPI.getCurrentInquiryFromUser(userID, {
          lang,
        });

        if (!alive) return;

        if (!inquiry || !inquiry.items?.length) {
          nav("/詢價", { replace: true });
          return;
        }

        setInq(inquiry);
      } catch (e) {
        console.error("load inquiry failed:", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [userID, lang, nav]);

  useEffect(() => {
    if (!user) return;

    const name = String(user?.name || user?.viewer?.name || "").trim();
    const phone = String(
      user?.phoneNumber || user?.viewer?.phoneNumber || "",
    ).trim();
    const email = String(user?.email || user?.viewer?.email || "").trim();

    setSuggest({
      姓名: uniqNonBlank([name]),
      聯絡電話: uniqNonBlank([phone]),
      電子郵件: uniqNonBlank([email]),
    });

    setForm((prev) => {
      const next = { ...prev };
      if (isBlank(next.姓名) && name) next.姓名 = name;
      if (isBlank(next.聯絡電話) && phone) next.聯絡電話 = phone;
      if (isBlank(next.電子郵件) && email) next.電子郵件 = email;
      return next;
    });
  }, [user]);

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm((s) => ({ ...s, [k]: val }));
  };

  const requiredKeys = useMemo(
    () => [
      "姓名",
      "聯絡電話",
      "電子郵件",
      "公司名稱",
      "縣市",
      "地址",
      "產業別",
    ],
    [],
  );

  const hasAnyEmptyRequired = useMemo(() => {
    return requiredKeys.some((k) => isBlank(form[k]));
  }, [form, requiredKeys]);

  const emailFormatInvalid = useMemo(() => {
    if (isBlank(form.電子郵件)) return false;
    return !isValidEmail(form.電子郵件);
  }, [form.電子郵件]);

  const phoneFormatInvalid = useMemo(() => {
    if (isBlank(form.聯絡電話)) return false;
    return !isValidPhone(form.聯絡電話);
  }, [form.聯絡電話]);

  const hasAnyInvalid = useMemo(() => {
    return hasAnyEmptyRequired || emailFormatInvalid || phoneFormatInvalid;
  }, [hasAnyEmptyRequired, emailFormatInvalid, phoneFormatInvalid]);

  const fieldErrorRequired = (k) => attempted && isBlank(form[k]);

  const emailHelperText = () => {
    if (!attempted) return " ";
    if (isBlank(form.電子郵件)) return t.required[lang];
    if (emailFormatInvalid) return t.invalidEmail[lang];
    return " ";
  };

  const phoneHelperText = () => {
    if (!attempted) return " ";
    if (isBlank(form.聯絡電話)) return t.required[lang];
    if (phoneFormatInvalid) return t.invalidPhone[lang];
    return " ";
  };

  const openConfirmOrValidate = () => {
    setAttempted(true);

    if (hasAnyInvalid) {
      const el = document.querySelector('[data-required-error="1"]');
      if (el?.scrollIntoView)
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setConfirmOpen(true);
  };

  const doSubmit = async () => {
    if (!inq || submitting) return;

    setAttempted(true);
    if (hasAnyInvalid) return;

    const emailNormalized = normalizeEmail(form.電子郵件);
    if (!emailNormalized || !isValidEmail(emailNormalized)) return;

    const payload = {
      userID,
      inquiryID: inq.inquiryID,

      info: {
        ...form,

        // ✅ ensure backend always gets a clean email
        電子郵件: emailNormalized,
        email: emailNormalized,

        聯絡電話: String(form.聯絡電話 || "").trim(),
        phone: String(form.聯絡電話 || "").trim(),
        姓名: String(form.姓名 || "").trim(),
        name: String(form.姓名 || "").trim(),
      },

      // ✅ keep flat fields too because checkout currently uses them
      ...form,
      電子郵件: emailNormalized,
      email: emailNormalized,
      聯絡電話: String(form.聯絡電話 || "").trim(),
      phone: String(form.聯絡電話 || "").trim(),
      name: String(form.姓名 || "").trim(),
    };

    setSubmitting(true);
    try {
      // 1) Save info
      await InquiryAPI.addInfo(payload, { lang });

      // 2) Checkout
      try {
        await InquiryAPI.checkout(inq.inquiryID, payload, { lang });
      } catch (err) {
        const msg = String(err?.message || "");
        const field = err?.response?.data?.field;

        if (
          field === "電子郵件" ||
          msg.includes("Customer email is required")
        ) {
          await InquiryAPI.addInfo(payload, { lang });
          await InquiryAPI.checkout(inq.inquiryID, payload, { lang });
        } else {
          throw err;
        }
      }

      showSnackbar(t.snackbar.success[lang], "success");
      setConfirmOpen(false);
      setSubmitted(true);

      window.dispatchEvent(
        new CustomEvent("inquiry:updated", {
          detail: { reason: "checkedout" },
        }),
      );
    } catch (e) {
      console.error(e);
      showSnackbar(t.snackbar.fail[lang], "error");
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SuccessInquiry />;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: 1080 },
        mx: "auto",
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        boxSizing: "border-box",
      }}
    >
      <Backdrop
        open={submitting}
        sx={{
          zIndex: (t2) => t2.zIndex.modal + 10,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(17,24,39,0.85)",
            px: 3,
            py: 2.5,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={22} />
          <Typography sx={{ fontWeight: 800, fontFamily: font[lang] }}>
            {lang === "ZH"
              ? "送出中…"
              : lang === "JP"
                ? "送信中…"
                : "Submitting..."}
          </Typography>
        </Box>
      </Backdrop>

      <Typography
        variant="h3"
        align="center"
        sx={{
          fontWeight: 700,
          fontFamily: font[lang],
          color: "#F5A400",
          mb: 2,
        }}
      >
        {t.title[lang]}
      </Typography>

      <Box
        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}
      >
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f6f6f6", minWidth: 0 }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontFamily: font[lang] }}
          >
            Step1 <Chip size="small" sx={{ ml: 1 }} />
          </Typography>
          <Typography
            sx={{ mt: 1, color: "text.secondary", fontFamily: font[lang] }}
          >
            {t.step1Title[lang]}
          </Typography>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f6f6f6", minWidth: 0 }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontFamily: font[lang] }}
          >
            Step2 <Chip color="primary" size="small" sx={{ ml: 1 }} />
          </Typography>
          <Typography
            sx={{ mt: 1, color: "text.secondary", fontFamily: font[lang] }}
          >
            {t.step2Title[lang]}
          </Typography>
        </Paper>
      </Box>

      <Paper
        variant="outlined"
        sx={{ mb: 3, bgcolor: "#fff", boxShadow: "none" }}
      >
        {isMdUp ? (
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="medium" sx={{ minWidth: 760 }}>
              <TableHead sx={{ bgcolor: "#fafafa" }}>
                <TableRow>
                  <TableCell width={80}>{t.table.no[lang]}</TableCell>
                  <TableCell width={120}>{t.table.image[lang]}</TableCell>
                  <TableCell width={160}>{t.table.series[lang]}</TableCell>
                  <TableCell>{t.table.name[lang]}</TableCell>
                  <TableCell width={120}>{t.table.qty[lang]}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(inq?.items || []).map((r, i) => {
                  const imgs = extractImages(r);
                  const thumb = imgs[0] || PLACEHOLDER_IMG;

                  return (
                    <TableRow key={r.productID || i}>
                      <TableCell>{i + 1}</TableCell>

                      <TableCell>
                        <Tooltip title={t.viewImg[lang]} placement="top">
                          <Box
                            component="button"
                            onClick={() => openImageDialog(r)}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: "pointer",
                            }}
                          >
                            <Box
                              component="img"
                              src={thumb}
                              alt={r.productName || r.productID}
                              onError={(e) =>
                                (e.currentTarget.src = PLACEHOLDER_IMG)
                              }
                              sx={{
                                width: 78,
                                height: 58,
                                objectFit: "contain",
                                display: "block",
                                borderRadius: 1,
                                bgcolor: "#fff",
                                p: 0.5,
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>

                      <TableCell>{r.productID}</TableCell>

                      <TableCell>
                        {r?.parentID ? (
                          <MLink
                            component="button"
                            underline="hover"
                            onClick={() => goToProduct(r)}
                            sx={{
                              fontFamily: font[lang],
                              cursor: "pointer",
                              color: "primary.main",
                              textAlign: "left",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {r.productName}
                          </MLink>
                        ) : (
                          <Typography sx={{ fontFamily: font[lang] }}>
                            {r.productName}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>{r.qty}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 1.25 }}>
            <Stack spacing={1.25}>
              {(inq?.items || []).map((r, i) => (
                <MobileInquiryItemCard
                  key={r.productID || i}
                  row={r}
                  lang={lang}
                  t={t}
                  onOpenImages={() => openImageDialog(r)}
                  onGoProduct={() => goToProduct(r)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 2.5, md: 3 }, boxShadow: "none", bgcolor: "#fff" }}
      >
        <Stack spacing={2}>
          <Autocomplete
            freeSolo
            options={suggest.姓名 || []}
            value={form.姓名 || ""}
            inputValue={form.姓名 || ""}
            onInputChange={(e, v) => setForm((s) => ({ ...s, 姓名: v }))}
            onChange={(e, v) =>
              setForm((s) => ({ ...s, 姓名: String(v ?? "") }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t.fields.name[lang]}
                required
                fullWidth
                error={fieldErrorRequired("姓名")}
                helperText={fieldErrorRequired("姓名") ? t.required[lang] : " "}
                inputProps={{
                  ...params.inputProps,
                  "data-required-error": fieldErrorRequired("姓名") ? 1 : 0,
                }}
              />
            )}
          />

          <Autocomplete
            freeSolo
            options={suggest.聯絡電話 || []}
            value={form.聯絡電話 || ""}
            inputValue={form.聯絡電話 || ""}
            onInputChange={(e, v) => setForm((s) => ({ ...s, 聯絡電話: v }))}
            onChange={(e, v) =>
              setForm((s) => ({ ...s, 聯絡電話: String(v ?? "") }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t.fields.phone[lang]}
                required
                fullWidth
                error={
                  attempted && (isBlank(form.聯絡電話) || phoneFormatInvalid)
                }
                helperText={phoneHelperText()}
                inputProps={{
                  ...params.inputProps,
                  "data-required-error":
                    attempted && (isBlank(form.聯絡電話) || phoneFormatInvalid)
                      ? 1
                      : 0,
                }}
                placeholder="09xxxxxxxxx"
              />
            )}
          />

          <Autocomplete
            freeSolo
            options={suggest.電子郵件 || []}
            value={form.電子郵件 || ""}
            inputValue={form.電子郵件 || ""}
            onInputChange={(e, v) => setForm((s) => ({ ...s, 電子郵件: v }))}
            onChange={(e, v) =>
              setForm((s) => ({ ...s, 電子郵件: String(v ?? "") }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t.fields.email[lang]}
                type="email"
                required
                fullWidth
                error={
                  attempted && (isBlank(form.電子郵件) || emailFormatInvalid)
                }
                helperText={emailHelperText()}
                inputProps={{
                  ...params.inputProps,
                  "data-required-error":
                    attempted && (isBlank(form.電子郵件) || emailFormatInvalid)
                      ? 1
                      : 0,
                }}
                placeholder="abc@gmail.com"
              />
            )}
          />

          <TextField
            label={t.fields.company[lang]}
            value={form.公司名稱}
            onChange={set("公司名稱")}
            required
            fullWidth
            error={fieldErrorRequired("公司名稱")}
            helperText={fieldErrorRequired("公司名稱") ? t.required[lang] : " "}
            inputProps={{
              "data-required-error": fieldErrorRequired("公司名稱") ? 1 : 0,
            }}
          />

          <TextField
            select
            label={t.fields.county[lang]}
            value={form.縣市}
            onChange={set("縣市")}
            required
            fullWidth
            error={fieldErrorRequired("縣市")}
            helperText={fieldErrorRequired("縣市") ? t.required[lang] : " "}
            inputProps={{
              "data-required-error": fieldErrorRequired("縣市") ? 1 : 0,
            }}
          >
            {COUNTIES_ZH.map((zh) => (
              <MenuItem key={zh} value={zh}>
                {byLangCounty(zh, lang)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t.fields.address[lang]}
            value={form.地址}
            onChange={set("地址")}
            required
            fullWidth
            error={fieldErrorRequired("地址")}
            helperText={fieldErrorRequired("地址") ? t.required[lang] : " "}
            inputProps={{
              "data-required-error": fieldErrorRequired("地址") ? 1 : 0,
            }}
          />

          {/* ✅ UPDATED: Industry field -> dropdown (value stays Mandarin) */}
          <TextField
            select
            label={t.fields.industry[lang]}
            value={form.產業別}
            onChange={set("產業別")}
            required
            fullWidth
            error={fieldErrorRequired("產業別")}
            helperText={fieldErrorRequired("產業別") ? t.required[lang] : " "}
            inputProps={{
              "data-required-error": fieldErrorRequired("產業別") ? 1 : 0,
            }}
            SelectProps={{
              renderValue: (val) => {
                const zhVal = String(val || "");
                const opt = INDUSTRY_OPTIONS.find((x) => x.ZH === zhVal);
                return opt ? industryLabelByLang(opt, lang) : zhVal;
              },
            }}
          >
            {INDUSTRY_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.ZH}
                value={opt.ZH}
                sx={{ fontFamily: font[lang] }}
              >
                {industryLabelByLang(opt, lang)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t.fields.message[lang]}
            value={form.訊息}
            onChange={set("訊息")}
            fullWidth
            multiline
            minRows={6}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            justifyContent="flex-end"
            sx={{ pt: 1 }}
          >
            <Button
              variant="outlined"
              onClick={() => nav("/詢價")}
              fullWidth
              sx={{ maxWidth: { xs: "100%", sm: 200 }, fontFamily: font[lang] }}
              disabled={submitting}
            >
              {t.actions.back[lang]}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={openConfirmOrValidate}
              disabled={!inq || submitting}
              fullWidth
              sx={{ maxWidth: { xs: "100%", sm: 200 }, fontFamily: font[lang] }}
            >
              {t.actions.submit[lang]}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontFamily: font[lang], fontWeight: 700 }}>
          {t.confirm.title[lang]}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: font[lang] }}>
            {t.confirm.desc[lang]}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={submitting}
            sx={{ fontFamily: font[lang] }}
          >
            {t.confirm.cancel[lang]}
          </Button>
          <Button
            variant="contained"
            onClick={doSubmit}
            disabled={submitting}
            sx={{ fontFamily: font[lang] }}
          >
            {t.confirm.confirm[lang]}
          </Button>
        </DialogActions>
      </Dialog>

      <ImageGalleryDialog
        open={imgDlgOpen}
        onClose={() => setImgDlgOpen(false)}
        title={imgDlgTitle}
        images={imgDlgImages}
        lang={lang}
      />
    </Box>
  );
}
